import { z } from 'zod';
import { QuestionnaireScheduleSchema } from '../scheduling/CreateSchedulingsForQuestionnaireDTO';
import { validateConditions } from '../conditions';
import { QuestionSchema, sortQuestionsByOrder } from './question';

export const QuestionsSchema = z
  .array(QuestionSchema)
  .superRefine(uniqueQuestions)
  .superRefine(validateConditions)
  .transform(sortQuestionsByOrder);

export const QuestionnaireBaseSchema = z.object({
  id: z.string().uuid(),
  studyId: z.string().uuid(),
  title: z.string().nonempty(),
  createdAt: z.string().datetime(),
  questions: QuestionsSchema,
});

function uniqueQuestions(
  questions: { internalName: string; id: string }[],
  ctx: z.RefinementCtx,
) {
  const internalNameIndices = new Map<string, number[]>();
  const idIndices = new Map<string, number[]>();

  // First pass: collect all indices for each value
  for (const [index, question] of questions.entries()) {
    if (!internalNameIndices.has(question.internalName)) {
      internalNameIndices.set(question.internalName, []);
    }
    internalNameIndices.get(question.internalName)!.push(index);

    if (!idIndices.has(question.id)) {
      idIndices.set(question.id, []);
    }
    idIndices.get(question.id)!.push(index);
  }

  // Second pass: add issues for duplicates
  for (const [internalName, indices] of internalNameIndices) {
    if (indices.length > 1) {
      for (const index of indices) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: [index, 'internalName'],
          message: `Une autre question a le même nom interne: ${internalName}`,
        });
      }
    }
  }

  for (const [id, indices] of idIndices) {
    if (indices.length > 1) {
      for (const index of indices) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: [index, 'id'],
          message: `Une autre question a le même id: ${id}`,
        });
      }
    }
  }
}

export const EmaQuestionnaireSchema = QuestionnaireBaseSchema.extend({
  kind: z.literal('EMA'),
  schedule: QuestionnaireScheduleSchema.nullable(),
});

export const InitialQuestionnaireSchema = QuestionnaireBaseSchema.extend({
  kind: z.literal('INITIAL'),
});

export const QuestionnaireSchema = z.discriminatedUnion('kind', [
  InitialQuestionnaireSchema,
  EmaQuestionnaireSchema,
]);

export type Questionnaire = z.infer<typeof QuestionnaireSchema>;

export type EmaQuestionnaire = z.infer<typeof EmaQuestionnaireSchema>;
export type InitialQuestionnaire = z.infer<typeof InitialQuestionnaireSchema>;
